/**
 * Geometry helpers for the address-to-district lookup.
 *
 * These run entirely client-side against the district polygons and school points
 * the map has already loaded — no extra network calls, and guaranteed consistent
 * with what's drawn on screen.
 */

import type {
  DistrictPolygonsResponse,
  DistrictPolygonFeature,
  SchoolPointFeature,
  SchoolPointsResponse,
} from '../../api/map';

type Ring = number[][]; // array of [lng, lat] positions

/** Even-odd ray casting for a single linear ring. */
function pointInRing(lng: number, lat: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring AND outside every hole ring. */
function pointInPolygonRings(lng: number, lat: number, rings: Ring[]): boolean {
  if (rings.length === 0) return false;
  if (!pointInRing(lng, lat, rings[0])) return false;
  for (let h = 1; h < rings.length; h++) {
    if (pointInRing(lng, lat, rings[h])) return false; // landed in a hole
  }
  return true;
}

export function pointInGeometry(
  lng: number,
  lat: number,
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
): boolean {
  if (geometry.type === 'Polygon') {
    return pointInPolygonRings(lng, lat, geometry.coordinates as Ring[]);
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as Ring[][]).some((poly) =>
      pointInPolygonRings(lng, lat, poly)
    );
  }
  return false;
}

/** All AIU member districts whose boundary contains the point (usually 0 or 1). */
export function findContainingDistricts(
  lng: number,
  lat: number,
  polygons: DistrictPolygonsResponse
): DistrictPolygonFeature[] {
  return polygons.features.filter((f) =>
    pointInGeometry(lng, lat, f.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon)
  );
}

const EARTH_RADIUS_MI = 3958.8;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface NearbySchool {
  feature: SchoolPointFeature;
  distanceMiles: number;
}

/** Closest `count` schools to the point, nearest first. */
export function nearestSchools(
  lat: number,
  lng: number,
  schools: SchoolPointsResponse | null,
  count = 3
): NearbySchool[] {
  if (!schools) return [];
  return schools.features
    .map((feature) => {
      const [slng, slat] = feature.geometry.coordinates;
      return { feature, distanceMiles: haversineMiles(lat, lng, slat, slng) };
    })
    .sort((a, b) => a.distanceMiles - b.distanceMiles)
    .slice(0, count);
}
